import React, { useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Typography, CircularProgress } from '@mui/material';

const ITEM_HEIGHT = 32;
const HEADER_HEIGHT = 34;
const OVERSCAN = 8;

/**
 * Windowed checkbox list. Intune tenants routinely return several thousand apps
 * and rendering a checkbox per row locks the browser up, so only the visible
 * slice is mounted.
 *
 * `sections` is [{ title?, items: [{ id, displayName }] }] - a single untitled
 * section covers the flat case.
 */
const VirtualizedCheckList = ({
  sections,
  selectedIds,
  onToggle,
  loading = false,
  height = 280,
  emptyMessage = 'No items match your filter.'
}) => {
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);

  const selectedSet = useMemo(
    () => (selectedIds instanceof Set ? selectedIds : new Set(selectedIds)),
    [selectedIds]
  );

  // Flatten sections into a single row list with cumulative offsets so we can
  // window across headers and items together.
  const { rows, offsets, totalHeight } = useMemo(() => {
    const flat = [];
    (sections || []).forEach((section, sectionIndex) => {
      if (section.title) {
        flat.push({ kind: 'header', key: `h-${sectionIndex}`, title: section.title });
      }
      (section.items || []).forEach((item) => {
        flat.push({ kind: 'item', key: item.id, item });
      });
    });

    const cumulative = new Array(flat.length + 1);
    cumulative[0] = 0;
    flat.forEach((row, i) => {
      cumulative[i + 1] = cumulative[i] + (row.kind === 'header' ? HEADER_HEIGHT : ITEM_HEIGHT);
    });

    return { rows: flat, offsets: cumulative, totalHeight: cumulative[flat.length] };
  }, [sections]);

  const itemCount = useMemo(
    () => (sections || []).reduce((sum, section) => sum + (section.items?.length || 0), 0),
    [sections]
  );

  // Binary search for the first row intersecting the viewport.
  const findRowAt = (y) => {
    let low = 0;
    let high = rows.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if (offsets[mid + 1] <= y) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    return Math.min(low, Math.max(rows.length - 1, 0));
  };

  const startIndex = Math.max(0, findRowAt(scrollTop) - OVERSCAN);
  const endIndex = Math.min(rows.length, findRowAt(scrollTop + height) + OVERSCAN + 1);
  const visible = rows.slice(startIndex, endIndex);

  return (
    <Box
      ref={scrollRef}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      sx={{
        height,
        overflowY: 'auto',
        overflowX: 'hidden',
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
        p: 1
      }}
    >
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading...</Typography>
        </Box>
      )}

      {!loading && itemCount === 0 && (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      )}

      {!loading && itemCount > 0 && (
        <Box sx={{ height: totalHeight, position: 'relative' }}>
          {visible.map((row, i) => {
            const index = startIndex + i;
            const top = offsets[index];

            if (row.kind === 'header') {
              return (
                <Typography
                  key={row.key}
                  variant="subtitle2"
                  sx={{
                    position: 'absolute',
                    top,
                    left: 0,
                    right: 0,
                    height: HEADER_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 600,
                    color: 'primary.main'
                  }}
                >
                  {row.title}
                </Typography>
              );
            }

            const { item } = row;
            return (
              <Box
                key={row.key}
                onClick={() => onToggle(item.id)}
                sx={{
                  position: 'absolute',
                  top,
                  left: 0,
                  right: 0,
                  height: ITEM_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedSet.has(item.id)}
                  onChange={() => onToggle(item.id)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.5 }}
                />
                <Typography
                  variant="body2"
                  noWrap
                  title={item.displayName}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {item.displayName}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default VirtualizedCheckList;
